import {
  COMPLIANCE_RECORD_STATUS_LABELS,
} from '@sitewise/shared';
import {
  Button,
  Card,
  CardContent,
  ComplianceBadge,
  EmptyState,
  Skeleton,
  toast,
} from '@sitewise/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardCheck, Cpu, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  archiveDevice,
  listComplianceRecords,
  listDevices,
  type DeviceWithDeviceType,
  type RecordWithDeviceType,
} from '../../../api/compliance.js';
import { CreateDeviceSheet } from '../compliance/create-device-sheet.js';
import {
  cadenceLabel,
  formatDate,
  formatRelativeDays,
} from '../compliance/lib.js';
import { LogComplianceSheet } from '../compliance/log-sheet.js';

interface Props {
  buildingId: string;
}

export function BuildingComplianceTab({ buildingId }: Props) {
  const queryClient = useQueryClient();
  const [logRecord, setLogRecord] = useState<RecordWithDeviceType | null>(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [editDevice, setEditDevice] = useState<DeviceWithDeviceType | null>(null);

  async function handleDelete(d: DeviceWithDeviceType) {
    if (
      !confirm(
        `Delete ${d.label}? This removes the device and any open compliance items. Past logs are kept.`,
      )
    ) {
      return;
    }
    try {
      await archiveDevice(d.id);
      toast.success('Device deleted.');
      void queryClient.invalidateQueries({ queryKey: ['compliance'] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete device');
    }
  }

  const recordsQuery = useQuery({
    queryKey: ['compliance', 'records', { buildingId }],
    queryFn: () => listComplianceRecords({ buildingId, status: ['upcoming', 'due_soon', 'overdue'] }),
  });
  const devicesQuery = useQuery({
    queryKey: ['compliance', 'devices', { buildingId }],
    queryFn: () => listDevices({ buildingId }),
  });

  const records = recordsQuery.data?.records ?? [];
  const devices = devicesQuery.data?.devices ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Active compliance</h3>
          <p className="text-xs text-fg-muted">
            All records due, due soon, or overdue for this building.
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setOpenCreate(true)}>
            <Plus className="h-4 w-4" />
            Add device
          </Button>
          <Link to="/compliance/records">
            <Button size="sm" variant="ghost">
              All records →
            </Button>
          </Link>
        </div>
      </div>

      {recordsQuery.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : records.length === 0 ? (
        <Card>
          <CardContent className="p-6">
            <EmptyState
              icon={ClipboardCheck}
              title="No active records"
              description="Add devices to schedule inspections."
            />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="divide-y divide-border p-0 text-sm">
            {records.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 px-5 py-3">
                <Link
                  to={`/compliance/records/${r.id}`}
                  className="flex min-w-0 flex-1 items-center gap-3 hover:underline"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{r.device.label}</div>
                    <div className="truncate text-xs text-fg-muted">{r.deviceType.name}</div>
                  </div>
                </Link>
                <div className="text-right">
                  <div className="tabular-nums">{formatDate(r.dueAt)}</div>
                  <div className="text-xs text-fg-muted">{formatRelativeDays(r.dueAt)}</div>
                </div>
                <ComplianceBadge tone={r.tone} withDot>
                  {COMPLIANCE_RECORD_STATUS_LABELS[r.status]}
                </ComplianceBadge>
                <Button size="sm" onClick={() => setLogRecord(r)}>
                  Log
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="pt-4">
        <h3 className="text-sm font-semibold">Devices on file</h3>
        <p className="mb-2 text-xs text-fg-muted">
          {devices.length} {devices.length === 1 ? 'device' : 'devices'}
        </p>
        {devicesQuery.isLoading ? (
          <Skeleton className="h-12 w-full" />
        ) : devices.length === 0 ? (
          <Card>
            <CardContent className="p-6">
              <EmptyState
                icon={Cpu}
                title="No devices"
                description="Add the first device to start scheduling compliance."
              />
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="divide-y divide-border p-0 text-sm">
              {devices.map((d) => (
                <div
                  key={d.id}
                  className="grid grid-cols-[1fr_auto_auto_auto_auto_auto] items-center gap-3 px-5 py-2.5"
                >
                  <div className="min-w-0">
                    <div className="truncate font-medium">{d.label}</div>
                    <div className="truncate text-xs text-fg-muted">{d.deviceType.name}</div>
                  </div>
                  <span className="text-xs text-fg-secondary">
                    {cadenceLabel(
                      d.cadenceOverride ?? d.deviceType.defaultCadence,
                      d.customIntervalDaysOverride ?? d.deviceType.defaultCustomIntervalDays,
                    )}
                  </span>
                  <span className="tabular-nums text-xs text-fg-secondary">
                    {d.nextDueAt ? `due ${formatDate(d.nextDueAt)}` : '—'}
                  </span>
                  <span className="tabular-nums text-xs text-fg-muted">
                    {d.lastLoggedAt ? `last ${formatDate(d.lastLoggedAt)}` : 'never logged'}
                  </span>
                  <Button size="sm" variant="ghost" onClick={() => setEditDevice(d)}>
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => void handleDelete(d)}
                    className="text-danger hover:text-danger"
                  >
                    Delete
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>

      {logRecord ? (
        <LogComplianceSheet
          record={logRecord}
          onClose={() => setLogRecord(null)}
          onSubmitted={() => {
            setLogRecord(null);
            void queryClient.invalidateQueries({ queryKey: ['compliance'] });
          }}
        />
      ) : null}

      {openCreate ? (
        <CreateDeviceSheet
          buildingId={buildingId}
          onClose={() => setOpenCreate(false)}
          onSaved={() => {
            setOpenCreate(false);
            void queryClient.invalidateQueries({ queryKey: ['compliance'] });
          }}
        />
      ) : null}

      {editDevice ? (
        <CreateDeviceSheet
          device={editDevice}
          onClose={() => setEditDevice(null)}
          onSaved={() => {
            setEditDevice(null);
            void queryClient.invalidateQueries({ queryKey: ['compliance'] });
          }}
        />
      ) : null}
    </div>
  );
}
